import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, ChevronDown, Search } from 'lucide-react';
import { useState } from 'react';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { formatAmount, formatDate } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { MODE_LABELS, useRecurringTemplates, type RecurringTemplate } from '@/lib/recurring';
import {
  DOCUMENT_TEMPLATE_KINDS,
  documentPath,
  isDocumentTemplateKind,
  type DocumentTemplateKind,
} from '@/lib/recurring-documents';

export const Route = createFileRoute('/accounting/recurring/')({ component: RecurringList });

/** Screen A7 (document 06): recurring transaction templates, list pattern. */
function RecurringList() {
  const orgId = useOrgId();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const canEdit = me.data?.isStaff ?? false;
  const templates = useRecurringTemplates();
  const [q, setQ] = useState('');
  const [deleting, setDeleting] = useState<RecurringTemplate | null>(null);

  const pause = useMutation({
    mutationFn: ({ t, paused }: { t: RecurringTemplate; paused: boolean }) =>
      api<RecurringTemplate>(
        `/orgs/${orgId}/recurring-templates/${t.id}/${paused ? 'pause' : 'resume'}`,
        { method: 'POST' },
      ),
    onSuccess: async (t) => {
      await qc.invalidateQueries({ queryKey: ['recurring', orgId] });
      toast(
        t.paused
          ? `"${t.name}" paused.`
          : `"${t.name}" resumed${t.nextDate ? `; next on ${formatDate(t.nextDate)}` : ''}.`,
      );
    },
    onError: (e) => toast(e.message),
  });
  const remove = useMutation({
    mutationFn: (t: RecurringTemplate) =>
      api<void>(`/orgs/${orgId}/recurring-templates/${t.id}`, { method: 'DELETE' }),
    onSuccess: async (_v, t) => {
      await qc.invalidateQueries({ queryKey: ['recurring', orgId] });
      toast(`"${t.name}" deleted.`);
      setDeleting(null);
    },
  });

  if (!orgId)
    return <EmptyState title="Select a client" hint="Choose a client to see its templates." />;
  const rows = (templates.data ?? []).filter((t) =>
    q.trim() ? t.name.toLowerCase().includes(q.trim().toLowerCase()) : true,
  );
  const createDraft = useMutation({
    mutationFn: (t: RecurringTemplate) =>
      api<{ document: { id: string; kind: DocumentTemplateKind } }>(
        `/orgs/${orgId}/recurring-templates/${t.id}/use-document`,
        {
          method: 'POST',
          body: JSON.stringify(t.due && t.nextDate ? { occurrenceDate: t.nextDate } : {}),
        },
      ),
    onSuccess: async (r, t) => {
      await qc.invalidateQueries();
      toast(`Draft ${t.kindLabel.toLowerCase()} created from "${t.name}".`);
      void navigate({ to: documentPath(r.document.kind, r.document.id) as never });
    },
    onError: (e) => toast(e.message),
  });
  // journal entries open the form prefilled; documents are created as drafts to review
  const use = (t: RecurringTemplate) =>
    isDocumentTemplateKind(t.kind)
      ? createDraft.mutate(t)
      : void navigate({ to: '/accounting/journal-entries/new', search: { template: t.id } });
  const newMenu = (label: string) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="gap-1">
          {label} <ChevronDown className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <Link to="/accounting/recurring/new">Journal entry</Link>
        </DropdownMenuItem>
        {DOCUMENT_TEMPLATE_KINDS.map((k) => (
          <DropdownMenuItem key={k.kind} asChild>
            <Link to="/accounting/recurring/new" search={{ kind: k.kind }}>
              {k.label}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div>
      <PageHeader
        title="Recurring transactions"
        description="Templates that create transactions on a schedule, remind you when they are due, or wait until you use them."
        actions={canEdit ? newMenu('New') : null}
      />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <Search className="absolute top-2.5 left-2.5 size-4 text-muted-foreground" aria-hidden />
          <Input
            className="pl-8"
            placeholder="Search by template name"
            aria-label="Search by template name"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>
      {templates.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : templates.isError ? (
        <EmptyState title="Could not load templates" hint={templates.error.message} />
      ) : !templates.data.length ? (
        <EmptyState
          title="No recurring transactions yet"
          hint="Set up rent, retainers, subscriptions or accruals once and let them recur every period."
          action={canEdit ? newMenu('New template') : undefined}
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Template name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Interval</TableHead>
                <TableHead>Previous date</TableHead>
                <TableHead>Next date</TableHead>
                <TableHead>Customer/Vendor</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead className="w-[150px]">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow key={t.id} data-testid={`template-${t.name}`}>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {canEdit ? (
                        <Link
                          to="/accounting/recurring/$templateId"
                          params={{ templateId: t.id }}
                          className="font-medium hover:text-primary hover:underline"
                        >
                          {t.name}
                        </Link>
                      ) : (
                        <span className="font-medium">{t.name}</span>
                      )}
                      {t.paused ? <Badge variant="secondary">Paused</Badge> : null}
                      {t.due && t.mode === 'reminder' ? <Badge variant="outline">Due</Badge> : null}
                    </div>
                    {t.lastError ? (
                      <p className="mt-1 flex items-start gap-1 text-xs text-destructive">
                        <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                        <span>
                          Could not create the {t.nextDate ? formatDate(t.nextDate) : ''}{' '}
                          {t.kind === 'journal_entry' ? 'entry' : t.kindLabel.toLowerCase()}:{' '}
                          {t.lastError}
                        </span>
                      </p>
                    ) : null}
                  </TableCell>
                  <TableCell>{t.kindLabel}</TableCell>
                  <TableCell>{t.interval ?? '—'}</TableCell>
                  <TableCell>{t.previousDate ? formatDate(t.previousDate) : ''}</TableCell>
                  <TableCell>{t.nextDate && !t.paused ? formatDate(t.nextDate) : ''}</TableCell>
                  <TableCell>{t.contactName ?? ''}</TableCell>
                  <TableCell className="tnum text-right">
                    {t.currency} {formatAmount(t.amount)}
                  </TableCell>
                  <TableCell>{MODE_LABELS[t.mode]}</TableCell>
                  <TableCell>
                    {canEdit ? (
                      <div className="flex items-center">
                        {t.due && t.mode === 'reminder' ? (
                          <Button variant="link" size="sm" className="px-1" onClick={() => use(t)}>
                            Use
                          </Button>
                        ) : (
                          <Button variant="link" size="sm" className="px-1" asChild>
                            <Link
                              to="/accounting/recurring/$templateId"
                              params={{ templateId: t.id }}
                            >
                              Edit
                            </Link>
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label={`More actions for ${t.name}`}
                            >
                              <ChevronDown className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="z-50">
                            <DropdownMenuItem
                              onSelect={() =>
                                void navigate({
                                  to: '/accounting/recurring/$templateId',
                                  params: { templateId: t.id },
                                })
                              }
                            >
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => use(t)}>Use</DropdownMenuItem>
                            <DropdownMenuItem
                              onSelect={() =>
                                void navigate({
                                  to: '/accounting/recurring/new',
                                  search: { copyOf: t.id },
                                })
                              }
                            >
                              Duplicate
                            </DropdownMenuItem>
                            {t.mode !== 'unscheduled' ? (
                              <DropdownMenuItem
                                onSelect={() => pause.mutate({ t, paused: !t.paused })}
                              >
                                {t.paused ? 'Resume' : 'Pause'}
                              </DropdownMenuItem>
                            ) : null}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem variant="destructive" onSelect={() => setDeleting(t)}>
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Dialog
        open={Boolean(deleting)}
        onOpenChange={(o) => (!o ? (setDeleting(null), remove.reset()) : undefined)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete “{deleting?.name}”?</DialogTitle>
            <DialogDescription>
              The template stops creating transactions. Transactions it already created stay in the
              books.
            </DialogDescription>
          </DialogHeader>
          {remove.error ? (
            <p role="alert" className="text-sm text-destructive">
              {remove.error.message}
            </p>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting)}
            >
              {remove.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
